import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { loadVerbsCsv } from './utils/loadVerbsCsv';
import { buildImperativeFromRow } from './utils/imperativeFormI_salim';
import { buildImperativeRoundPlan } from './utils/imperativeRoundPlan';
import manSingleIcon from './imgs/man-single.svg';
import womanSingleIcon from './imgs/woman-single.svg';
import menGroupIcon from './imgs/men-group.svg';
import womenGroupIcon from './imgs/women-group.svg';
import html2canvas from 'html2canvas';
import './ImperativeMatchingGame.css';

const PRONOUNS = [
  { id: 'you_ms', emoji: '🕺🏽', icon: manSingleIcon, nameHeb: 'אתה', nameAr: 'أَنْتَ' },
  { id: 'you_fs', emoji: '💃', icon: womanSingleIcon, nameHeb: 'את', nameAr: 'أَنْتِ' },
  { id: 'you_mp', emoji: '👨‍👦‍👦', icon: menGroupIcon, nameHeb: 'אתם', nameAr: 'أَنْتُمْ' },
  { id: 'you_fp', emoji: '👩‍👧‍👧', icon: womenGroupIcon, nameHeb: 'אתן', nameAr: 'أَنْتُنَّ' }
];

const TOTAL_ROUNDS = 6;

function ImperativeMatchingGame() {
  const navigate = useNavigate();
  const { grade } = useParams();
  const location = useLocation();
  const fromBinyan = location.state?.fromBinyan;
  
  const getBackUrl = () => {
    if (fromBinyan) {
      return `/grade/${grade}/verbs/binyan/${fromBinyan}`;
    }
    return `/grade/${grade}/verbs`;
  };
  
  const [currentRound, setCurrentRound] = useState(0);
  const [verbs, setVerbs] = useState([]);
  const [currentVerbs, setCurrentVerbs] = useState([]);
  const [matches, setMatches] = useState({});
  const [isDrawing, setIsDrawing] = useState(false);
  const [lineStart, setLineStart] = useState(null);
  const [currentLine, setCurrentLine] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [incorrectMatches, setIncorrectMatches] = useState(new Set());
  const [gameHistory, setGameHistory] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [showResultsPopup, setShowResultsPopup] = useState(false);
  const [showFixPrompt, setShowFixPrompt] = useState(false);
  const [pulseLines, setPulseLines] = useState(new Set());
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [isFixMode, setIsFixMode] = useState(false);
  const [hasEditedAfterFixStart, setHasEditedAfterFixStart] = useState(false);
  const [correctedVerbs, setCorrectedVerbs] = useState(new Set());
  const [firstAttemptMatches, setFirstAttemptMatches] = useState(null);
  const [preventScroll, setPreventScroll] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  
  const svgRef = useRef(null);
  const verbRefs = useRef({});
  const pronounRefs = useRef({});
  const resultsSummaryRef = useRef(null);
  const audioContextRef = useRef(null);
  const scrollPositionRef = useRef({ x: 0, y: 0 });

  // Scroll-lock pattern: preserves scroll position during line drawing on mobile only
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    
    if (preventScroll && isMobile) {
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current.y}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else if (isMobile) {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollPositionRef.current.y);
    }

    return () => {
      if (window.innerWidth <= 768) {
        const savedScroll = scrollPositionRef.current;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, savedScroll.y);
      }
    };
  }, [preventScroll]);

  const initAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
  };

  useEffect(() => {
    return () => {
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    const loadVerbs = async () => {
      try {
        const rows = await loadVerbsCsv('/verbs-grade10.csv');
        
        // סינון לבניין 1 בגזרת השלמים בלבד
        const filteredVerbs = rows.filter(r => {
          const binyan = (r['בניין'] || '').trim();
          const gizra = (r['gizra'] || '').trim();
          const tense = r['זמן'];
          const faVowelFuture = r['R2_FUTURE'] || r['fa_vowel_future'];
          
          return (binyan === '1' || binyan === 'בניין 1') && 
                 gizra.includes('שלמים') && 
                 tense === 'עבר' &&
                 faVowelFuture && faVowelFuture.trim() !== '';
        });

        const selected = buildImperativeRoundPlan(filteredVerbs, PRONOUNS, TOTAL_ROUNDS);
        
        setVerbs(selected);
        loadRound(0, selected);
      } catch (error) {
        console.error('שגיאה בטעינת פעלים:', error);
      }
    };
    
    loadVerbs();
  }, []);

  const loadRound = (roundIndex, allVerbs) => {
    if (roundIndex >= TOTAL_ROUNDS) {
      setShowResults(true);
      return;
    }

    // לקיחת 4 פעלים לסיבוב הנוכחי
    const startIdx = roundIndex * 4;
    const roundVerbs = allVerbs.slice(startIdx, startIdx + 4);

    // בניית צורות ציווי וקישור לגופים
    const verbsWithImperative = roundVerbs.map((assignment, idx) => {
      const verb = assignment.verb || assignment;
      const imperatives = buildImperativeFromRow(verb);
      const pronoun = assignment.pronoun || PRONOUNS[idx]; // כל פועל מקבל גוף שונה
      
      return {
        id: `verb_${roundIndex}_${idx}`,
        verbData: verb,
        imperative: imperatives ? imperatives[pronoun.id] : null,
        correctPronoun: pronoun.id,
        translation: verb['translation1_he_past'] || verb['translation2_he_past'] || ''
      };
    });

    // ערבוב הפעלים (אבל לא הדמויות)
    const shuffledVerbs = [...verbsWithImperative].sort(() => Math.random() - 0.5);

    setCurrentVerbs(shuffledVerbs);
    setMatches({});
    setIncorrectMatches(new Set());
    setShowFeedback(false);
    setAttempts(0);
    setShowFixPrompt(false);
    setPulseLines(new Set());
    setShowCorrectAnswer(false);
    setIsFixMode(false);
    setHasEditedAfterFixStart(false);
    setCorrectedVerbs(new Set());
    setFirstAttemptMatches(null);
  };

  const getVerbPosition = (verbId) => {
    const element = verbRefs.current[verbId];
    if (!element) return null;
    
    // נקודת החיבור תהיה ממרכז הרדיו באטון של הפועל
    const radioButton = element.querySelector('.verb-radio');
    const rect = radioButton ? radioButton.getBoundingClientRect() : element.getBoundingClientRect();
    const svgRect = svgRef.current?.getBoundingClientRect();
    
    if (!svgRect) return null;
    
    return {
      x: rect.left - svgRect.left + rect.width / 2,
      y: rect.top - svgRect.top + rect.height / 2
    };
  };

  const getPronounPosition = (pronounId) => {
    const element = pronounRefs.current[pronounId];
    if (!element) return null;
    
    // נקודת החיבור תהיה ממרכז הרדיו באטון
    const radioButton = element.querySelector('.pronoun-radio');
    const rect = radioButton ? radioButton.getBoundingClientRect() : element.getBoundingClientRect();
    const svgRect = svgRef.current?.getBoundingClientRect();
    
    if (!svgRect) return null;
    
    return {
      x: rect.left - svgRect.left + rect.width / 2,
      y: rect.top - svgRect.top + rect.height / 2
    };
  };

  const handleVerbMouseDown = (verbId, e) => {
    if (e && e.cancelable) {
      e.preventDefault();
    }
    initAudioContext();
    
    // אם הפועל כבר מחובר, מחק את הקו הקיים
    if (matches[verbId]) {
      if (isFixMode) {
        setHasEditedAfterFixStart(true);
      }
      setMatches(prev => {
        const newMatches = { ...prev };
        delete newMatches[verbId];
        return newMatches;
      });
    }
    
    setIsDrawing(true);
    scrollPositionRef.current = { x: window.scrollX, y: window.scrollY };
    setPreventScroll(true);
    setLineStart({ type: 'verb', id: verbId });
    
    const pos = getVerbPosition(verbId);
    if (pos) {
      setCurrentLine({ start: pos, end: pos });
    }
  };

  const handlePronounMouseDown = (pronounId, e) => {
    if (e && e.cancelable) {
      e.preventDefault();
    }
    initAudioContext();
    
    // אם הגוף כבר מחובר לפועל כלשהו, מחק את הקו הקיים
    const connectedVerbId = Object.keys(matches).find(verbId => matches[verbId] === pronounId);
    if (connectedVerbId) {
      if (isFixMode) {
        setHasEditedAfterFixStart(true);
      }
      setMatches(prev => {
        const newMatches = { ...prev };
        delete newMatches[connectedVerbId];
        return newMatches;
      });
    }
    
    setIsDrawing(true);
    scrollPositionRef.current = { x: window.scrollX, y: window.scrollY };
    setPreventScroll(true);
    setLineStart({ type: 'pronoun', id: pronounId });
    
    const pos = getPronounPosition(pronounId);
    if (pos) {
      setCurrentLine({ start: pos, end: pos });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !lineStart) return;
    
    const svgRect = svgRef.current?.getBoundingClientRect();
    if (!svgRect) return;
    
    const startPos = lineStart.type === 'verb' 
      ? getVerbPosition(lineStart.id) 
      : getPronounPosition(lineStart.id);
    if (!startPos) return;
    
    setCurrentLine({
      start: startPos,
      end: {
        x: e.clientX - svgRect.left,
        y: e.clientY - svgRect.top
      }
    });
  };

  const handleTouchMove = (e) => {
    if (!isDrawing || !lineStart) return;
    
    if (e.cancelable) {
      e.preventDefault();
    }
    
    const svgRect = svgRef.current?.getBoundingClientRect();
    if (!svgRect) return;
    
    const touch = e.touches[0];
    if (!touch) return;
    
    const startPos = lineStart.type === 'verb' 
      ? getVerbPosition(lineStart.id) 
      : getPronounPosition(lineStart.id);
    if (!startPos) return;
    
    setCurrentLine({
      start: startPos,
      end: {
        x: touch.clientX - svgRect.left,
        y: touch.clientY - svgRect.top
      }
    });
  };

  const handlePronounMouseUp = (pronounId) => {
    if (!isDrawing || !lineStart) return;
    
    // אם התחלנו מפועל - מסיימים בגוף
    if (lineStart.type === 'verb') {
      if (isFixMode) {
        setHasEditedAfterFixStart(true);
      }
      setMatches(prev => {
        const newMatches = { ...prev };
        
        // מחיקת כל פועל אחר שמחובר לגוף הזה
        Object.keys(newMatches).forEach(verbId => {
          if (newMatches[verbId] === pronounId && verbId !== lineStart.id) {
            delete newMatches[verbId];
          }
        });
        
        // חיבור הפועל הנוכחי לגוף
        newMatches[lineStart.id] = pronounId;
        
        return newMatches;
      });
    }
    
    setIsDrawing(false);
    setLineStart(null);
    setCurrentLine(null);
    setPreventScroll(false);
  };

  const handleVerbMouseUp = (verbId) => {
    if (!isDrawing || !lineStart) return;
    
    // אם התחלנו מגוף - מסיימים בפועל
    if (lineStart.type === 'pronoun') {
      if (isFixMode) {
        setHasEditedAfterFixStart(true);
      }
      setMatches(prev => {
        const newMatches = { ...prev };
        
        // מחיקת כל פועל אחר שמחובר לגוף הזה
        Object.keys(newMatches).forEach(vId => {
          if (newMatches[vId] === lineStart.id && vId !== verbId) {
            delete newMatches[vId];
          }
        });
        
        // חיבור הפועל הנוכחי לגוף
        newMatches[verbId] = lineStart.id;
        
        return newMatches;
      });
    }
    
    setIsDrawing(false);
    setLineStart(null);
    setCurrentLine(null);
    setPreventScroll(false);
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
    setLineStart(null);
    setCurrentLine(null);
    setPreventScroll(false);
  };

  const handleTouchEnd = (e) => {
    if (!isDrawing || !lineStart) {
      setIsDrawing(false);
      setLineStart(null);
      setCurrentLine(null);
      setPreventScroll(false);
      return;
    }
    
    if (e.cancelable) {
      e.preventDefault();
    }
    
    const touch = e.changedTouches[0];
    if (!touch) {
      setIsDrawing(false);
      setLineStart(null);
      setCurrentLine(null);
      setPreventScroll(false);
      return;
    }
    
    const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
    
    for (const element of elements) {
      const verbId = element.closest('[data-verb-id]')?.getAttribute('data-verb-id');
      const pronounId = element.closest('[data-pronoun-id]')?.getAttribute('data-pronoun-id');
      
      if (lineStart.type === 'verb' && pronounId) {
        handlePronounMouseUp(pronounId);
        return;
      }
      
      if (lineStart.type === 'pronoun' && verbId) {
        handleVerbMouseUp(verbId);
        return;
      }
    }
    
    setIsDrawing(false);
    setLineStart(null);
    setCurrentLine(null);
    setPreventScroll(false);
  };

  const handleTouchCancel = () => {
    setIsDrawing(false);
    setLineStart(null);
    setCurrentLine(null);
    setPreventScroll(false);
  };

  const checkAnswers = () => {
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);
    
    const incorrect = new Set();
    let allCorrect = true;
    
    // בדיקה שכל הפעלים מחוברים
    const allMatched = currentVerbs.every(verb => matches[verb.id] !== undefined);
    
    if (!allMatched) {
      alert('יש לחבר את כל הפעלים לדמויות!');
      return;
    }
    
    // שמירת התשובה הראשונה של המשתמש אם זה הניסיון הראשון
    if (newAttempts === 1 && !firstAttemptMatches) {
      setFirstAttemptMatches({ ...matches });
    }
    
    // בדיקת התשובות
    currentVerbs.forEach(verb => {
      const userAnswer = matches[verb.id];
      if (userAnswer !== verb.correctPronoun) {
        incorrect.add(verb.id);
        allCorrect = false;
      }
    });
    
    setIncorrectMatches(incorrect);
    setShowFeedback(true);
    
    if (allCorrect) {
      playSuccessSound();
      setShowFireworks(true);
      // שמירה בהיסטוריה - יש טעות אם זה לא הניסיון הראשון
      const hadErrorInRound = newAttempts > 1;
      saveRoundToHistory(newAttempts, hadErrorInRound);
      
      // מעבר לסיבוב הבא אחרי 1.5 שניות
      setTimeout(() => {
        setShowFireworks(false);
        const nextRound = currentRound + 1;
        setCurrentRound(nextRound);
        loadRound(nextRound, verbs);
      }, 1500);
    } else {
      playErrorSound();
      setShowFixPrompt(true);
    }
  };

  // טיפול ב"תרצו לתקן"
  const handleFix = () => {
    setShowFixPrompt(false);
    setIsFixMode(true);
    setHasEditedAfterFixStart(false);
    setShowFeedback(false);
    // לא מנקים את incorrectMatches - המסגרות האדומות יישארו עד לבדיקה הבאה
  };

  // המשך ללא תיקון - הצג תשובה נכונה
  const handleContinue = () => {
    setShowFixPrompt(false);
    setShowCorrectAnswer(true);
    
    // זיהוי הקווים השגויים ותיקון התשובות
    const wrongVerbs = new Set();
    const correctedMatches = { ...matches };
    
    currentVerbs.forEach(verb => {
      const userAnswer = matches[verb.id];
      if (userAnswer !== verb.correctPronoun) {
        wrongVerbs.add(verb.id);
        correctedMatches[verb.id] = verb.correctPronoun;
      }
    });
    
    setMatches(correctedMatches);
    setCorrectedVerbs(wrongVerbs);
    setIncorrectMatches(new Set());
    setShowFeedback(true); // הפעלת מצב feedback כדי להציג את הירוק
    
    saveRoundToHistory(attempts, true);
  };

  // מעבר לסיבוב הבא אחרי צפייה בתשובה הנכונה
  const handleNextAfterCorrectAnswer = () => {
    setCorrectedVerbs(new Set());
    setShowCorrectAnswer(false);
    const nextRound = currentRound + 1;
    setCurrentRound(nextRound);
    loadRound(nextRound, verbs);
  };

  const saveRoundToHistory = (finalAttempts, hadError) => {
    setGameHistory(prev => [...prev, {
      round: currentRound + 1,
      verbs: currentVerbs,
      matches: matches,
      firstAttemptMatches: firstAttemptMatches ? { ...firstAttemptMatches } : null,
      attempts: finalAttempts,
      hadError: hadError
    }]);
  };

  const playSuccessSound = () => {
    try {
      if (!audioContextRef.current) return;
      const audioContext = audioContextRef.current;
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 523.25;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const playErrorSound = () => {
    try {
      if (!audioContextRef.current) return;
      const audioContext = audioContextRef.current;
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 200;
      oscillator.type = 'sawtooth';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const handleBackToGames = () => {
    navigate(getBackUrl());
  };

  const generateResultsImage = async () => {
    if (!resultsSummaryRef.current) return null;
    const canvas = await html2canvas(resultsSummaryRef.current, {
      backgroundColor: '#5eb9c5',
      scale: 2,
    });
    return canvas.toDataURL('image/png');
  };

  const dataURLtoBlob = (dataURL) => {
    const parts = dataURL.split(',');
    const mime = parts[0].match(/:(.*?);/)[1];
    const bstr = atob(parts[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new Blob([u8arr], { type: mime });
  };

  const shareViaWhatsApp = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;

    const blob = dataURLtoBlob(imageData);
    const fileName = 'תוצאות-ציווי-בניין1.png';
    const file = new File([blob], fileName, { type: 'image/png' });

    if (navigator.share && navigator.canShare({ files: [file] })) {
      try {
        const correctCount = gameHistory.filter(h => !h.hadError).length;
        
        await navigator.share({
          title: 'תוצאות צורת הציווי',
          text: `צורת הציווי - בניין 1\nהשגתי ${correctCount}/${TOTAL_ROUNDS}!`,
          files: [file],
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('שגיאה בשיתוף:', err);
        }
      }
    } else {
      const link = document.createElement('a');
      link.download = fileName;
      link.href = URL.createObjectURL(blob);
      link.click();
    }
    setShowResultsPopup(false);
  };

  const shareViaEmail = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;
    const fileName = 'תוצאות-ציווי-בניין1.png';
    const link = document.createElement('a');
    link.download = fileName;
    link.href = imageData;
    link.click();
    setShowResultsPopup(false);
  };

  if (showResults) {
    const correctCount = gameHistory.filter(h => !h.hadError).length;

    return (
      <div className="App">
        <header className="App-header">
          <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
            <button onClick={handleBackToGames} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
              חזרה למשחקי הפועל ↩
            </button>
            <h1 className="game-title-with-back" style={{ margin: 0 }}>תוצאות המשחק</h1>
          </div>
        </header>
        <main className="game-container results-screen">
          <div className="words-summary" ref={resultsSummaryRef}>
            <h3>צורת הציווי - בניין 1</h3>
            <div className="final-score-display">
              ניקוד: {correctCount}/{TOTAL_ROUNDS}
            </div>
            <br/>
            <div className="words-list">
              {gameHistory.map((round, roundIdx) => {
                // בניית רשימת התשובות הנכונות
                const correctAnswers = round.verbs.map(verb => {
                  const correctPronoun = PRONOUNS.find(p => p.id === verb.correctPronoun);
                  return `${verb.imperative} - ${correctPronoun?.nameHeb}`;
                }).join(', ');
                
                // אם היו טעויות, בנה את רשימת התשובות של המשתמש
                let userAnswersText = '';
                
                if (round.hadError && round.firstAttemptMatches) {
                  const userAnswers = [];
                  
                  round.verbs.forEach(verb => {
                    const userPronounId = round.firstAttemptMatches[verb.id];
                    const userPronoun = PRONOUNS.find(p => p.id === userPronounId);
                    
                    userAnswers.push(`${verb.imperative} - ${userPronoun?.nameHeb || '—'}`);
                  });
                  
                  userAnswersText = userAnswers.join(', ');
                }
                
                return (
                  <div key={roundIdx} className="word-item">
                    <div className="word-pair">
                      <span className="hebrew-word">{correctAnswers}</span>
                    </div>
                    <div className={`attempts-info ${round.hadError ? 'multiple-attempts' : ''}`}>
                      {!round.hadError ? (
                        <span>✓ תשובה נכונה</span>
                      ) : (
                        <span>✗ תשובתך: {userAnswersText}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="game-end-buttons">
              <button onClick={() => window.location.reload()} className="game-button restart-button">
                שחק מחדש
              </button>
              <button onClick={() => setShowResultsPopup(true)} className="game-button send-button">
                שתף תוצאות
              </button>
            </div>
          </div>

          {showResultsPopup && (
            <div className="popup-overlay" onClick={() => setShowResultsPopup(false)}>
              <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                <button className="popup-close-button" onClick={() => setShowResultsPopup(false)}>
                  ×
                </button>
                <h3>שתף את התוצאות</h3>
                <p>בחר איך תרצה לשתף את התוצאות שלך:</p>
                <div className="popup-buttons">
                  <button onClick={shareViaEmail} className="popup-button email-button">
                    💾 הורד תמונה
                  </button>
                  <button onClick={shareViaWhatsApp} className="popup-button whatsapp-button">
                    📱 שתף בוואטסאפ
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  if (currentVerbs.length === 0) {
    return (
      <div className="App">
        <header className="App-header">
          <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
            <button onClick={handleBackToGames} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
              חזרה למשחקי הפועל ↩
            </button>
            <h1 className="game-title-with-back" style={{ margin: 0 }}>טוען פעלים…</h1>
          </div>
        </header>
        <main className="game-container">
          <p>אנא בדוק את הקונסול (F12) לפרטי debug</p>
        </main>
      </div>
    );
  }

  const hasAllMatches = Object.keys(matches).length === currentVerbs.length;
  const isReadyToCheck = hasAllMatches && (!isFixMode || hasEditedAfterFixStart);

  return (
    <div className="App imperative-matching-game">
      <header className="App-header">
        <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
          <button onClick={handleBackToGames} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
            חזרה למשחקי הפועל ↩
          </button>
          <h1 className="game-title-with-back" style={{ margin: 0 }}>צורת הציווי - בניין 1</h1>
        </div>
        <p className="game-instructions-animate">משוך קו מכל פועל אל הדמות המתאימה</p>
      </header>
      <div className="verb-progress">{currentRound + 1}/{TOTAL_ROUNDS}</div>
      <main 
        className={`game-container imperative-game-board ${preventScroll ? 'prevent-scroll' : ''} ${isReadyToCheck || showCorrectAnswer ? 'ready-to-check' : ''}`}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
      >
        
        <svg 
          ref={svgRef} 
          className="lines-svg"
        >
          {/* קווים קבועים */}
          {Object.entries(matches).map(([verbId, pronounId]) => {
            const startPos = getVerbPosition(verbId);
            const endPos = getPronounPosition(pronounId);
            
            if (!startPos || !endPos) return null;
            
            const isIncorrect = incorrectMatches.has(verbId) && !isFixMode;
            const isCorrect = showFeedback && !incorrectMatches.has(verbId);
            const isPulsing = pulseLines.has(verbId);
            
            return (
              <line
                key={`${verbId}-${pronounId}`}
                x1={startPos.x}
                y1={startPos.y}
                x2={endPos.x}
                y2={endPos.y}
                stroke={isCorrect ? '#4CAF50' : isIncorrect ? '#f44336' : isPulsing ? '#ffeb3b' : '#7b0d99'}
                strokeWidth="3"
                className={`${isCorrect ? 'line-correct' : isIncorrect ? 'line-incorrect' : 'line-normal'} ${isPulsing ? 'line-pulse' : ''}`}
              />
            );
          })}
          
          {/* קו נמשך */}
          {currentLine && (
            <line
              x1={currentLine.start.x}
              y1={currentLine.start.y}
              x2={currentLine.end.x}
              y2={currentLine.end.y}
              stroke="#ffeb3b"
              strokeWidth="3"
              strokeDasharray="5,5"
              className="line-drawing"
            />
          )}
        </svg>

        {showFireworks && (
          <div className="fireworks-container">
            <div className="firework firework-1"></div>
            <div className="firework firework-2"></div>
            <div className="firework firework-3"></div>
            <div className="firework firework-4"></div>
            <div className="firework firework-5"></div>
            <div className="firework firework-6"></div>
            <div className="firework firework-7"></div>
            <div className="firework firework-8"></div>
            <div className="firework firework-9"></div>
            <div className="firework firework-10"></div>
            <div className="firework firework-11"></div>
            <div className="firework firework-12"></div>
          </div>
        )}

        <div className="verbs-column">
          {currentVerbs.map(verb => {
            const isIncorrect = incorrectMatches.has(verb.id);
            const wasCorrected = correctedVerbs.has(verb.id);
            
            let cssClass = '';
            if (showCorrectAnswer) {
              // במצב הצגת תשובה נכונה - הכל ירוק
              cssClass = wasCorrected ? 'correct pulse-correct' : 'correct';
            } else if (isIncorrect) {
              // במצב טעות
              cssClass = isFixMode ? 'incorrect-fixing' : 'incorrect';
            }
            
            return (
              <div
                key={verb.id}
                ref={el => verbRefs.current[verb.id] = el}
                className={`verb-item ${cssClass}`}
                data-verb-id={verb.id}
                onMouseDown={() => handleVerbMouseDown(verb.id)}
                onMouseUp={() => handleVerbMouseUp(verb.id)}
                onTouchStart={(e) => handleVerbMouseDown(verb.id, e)}
                style={{ cursor: 'pointer' }}
              >
                <div className="verb-content">
                  <div className="verb-arabic">{verb.imperative}</div>
                  <div className="verb-translation">{verb.translation}</div>
                </div>
                <div className="verb-radio"></div>
              </div>
            );
          })}
        </div>

        <div className="pronouns-column">
          {PRONOUNS.map(pronoun => {
            // מצא את הפועל שמחובר לגוף הזה
            const connectedVerb = currentVerbs.find(v => matches[v.id] === pronoun.id);
            const isCorrect = showFeedback && connectedVerb && !incorrectMatches.has(connectedVerb.id);
            
            return (
              <div
                key={pronoun.id}
                ref={el => pronounRefs.current[pronoun.id] = el}
                className={`pronoun-item ${isCorrect ? 'correct' : ''}`}
                data-pronoun-id={pronoun.id}
                onMouseDown={() => handlePronounMouseDown(pronoun.id)}
                onMouseUp={() => handlePronounMouseUp(pronoun.id)}
                onTouchStart={(e) => handlePronounMouseDown(pronoun.id, e)}
              >
                <div className="pronoun-radio"></div>
                <div className="pronoun-emoji">
                  {pronoun.icon ? (
                    <img className="pronoun-icon-image" src={pronoun.icon} alt="" />
                  ) : pronoun.emoji}
                </div>
                <div className="pronoun-name">{pronoun.nameAr}</div>
              </div>
            );
          })}
        </div>

        {!showCorrectAnswer && (
          <div className={`check-button-container ${isReadyToCheck ? 'ready-to-check' : ''}`}>
            <button 
              onClick={checkAnswers}
              className={`check-button ${isFixMode ? 'fix-mode' : ''}`}
              disabled={!isReadyToCheck || showFeedback || showFixPrompt}
            >
              בדוק תשובה
            </button>
          </div>
        )}

        {showCorrectAnswer && (
          <div className="check-button-container ready-to-check">
            <button 
              onClick={handleNextAfterCorrectAnswer}
              className="check-button"
            >
              המשך
            </button>
          </div>
        )}
      </main>

      {showFixPrompt && (
        <>
          <div className="overlay"></div>
          <div className="fix-prompt">
            <p>תרצו לתקן?</p>
            <div className="fix-buttons">
              <button onClick={handleFix} className="fix-yes">כן, אתקן</button>
              <button onClick={handleContinue} className="fix-no">לא, הצג תשובה נכונה</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ImperativeMatchingGame;
